import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnVisibilityState } from '@tanstack/react-table';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  tenantCloneJobDisposition,
  Tenant,
  TenantCloneJob,
  TenantListItem
} from '@bliks-io/contracts';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import {
  useCloneJobs,
  useDiscardCloneJob,
  useDismissCloneJob,
  useRefreshTenantsWhenACloneJobFinishes
} from '@/pages/admin/api/queries/useCloneJobs';
import { useDeleteTenant } from '@/pages/admin/api/mutations/useTenants';
import { usePlatformRoles } from '@/hooks/usePlatformRoles';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import {
  Badge,
  DataTable,
  createActionsColumn,
  createColumnHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { CreateTenantDialog } from './CreateTenantDialog';
import { CloneTenantDialog } from './CloneTenantDialog';
import { CloneJobAlert } from './components/CloneJobAlert';
import { CloneJobBadge } from './components/CloneJobBadge';
import { DeleteTenantConfirmDialog } from './components/DeleteTenantConfirmDialog';
import { DiscardCloneConfirmDialog } from './components/DiscardCloneConfirmDialog';
import { TenantTypeBadge } from './components/TenantTypeBadge';
import { TenantExpiryBadge } from './components/TenantExpiryBadge';

/**
 * Die Arbeitsbereichsliste — einmal gebaut, an jeder Stelle dieselbe.
 *
 * Die Verwaltung zeigt alle, die Kontoseite die eines Kunden. Gefiltert wird
 * davor, nicht darin, und mit der Tabelle kommt alles mit, was an einer Zeile
 * haengt: Kopierauftraege, Bearbeiten, Duplizieren, Loeschen samt ihren
 * Dialogen. Wer hier eine Spalte verbessert, verbessert sie an beiden Stellen.
 *
 * Der Einstieg „neuen Arbeitsbereich anlegen" bleibt bei der Seite: er gehoert
 * zu keiner Zeile, und jede Stelle legt ihn anders an.
 */
export function TenantsTable({
  tenants,
  tableId,
  initialColumnVisibility,
  keepsLayoutWhenEmpty
}: {
  tenants: TenantListItem[];
  /** Eigene Kennung je Stelle, damit sich Spaltenwahl nicht gegenseitig setzt. */
  tableId: string;
  initialColumnVisibility?: ColumnVisibilityState;
  /** Leer bleibt die Tabelle stehen, wo Listen nebeneinander stehen (Kontoseite). */
  keepsLayoutWhenEmpty?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Je Handlung ihr eigenes Recht (ADR 0042). Der Vertrieb sieht, DASS es
  // den Arbeitsbereich gibt — hinein und an ihn heran fuehrt ihn nichts, egal
  // von welcher Seite die Tabelle haengt.
  const { can } = usePlatformRoles();
  const mayOpen = can('readSensitive', 'PlatformTenant');
  const mayEdit = can('update', 'PlatformTenant');
  // Duplizieren liest den ganzen Arbeitsbereich und legt einen neuen an.
  // Klonen kopiert alle Daten eines Kunden: nur, wer Arbeitsbereiche ganz verwaltet.
  const mayClone = mayOpen && can('manage', 'PlatformTenant');
  const mayDelete = can('delete', 'PlatformTenant');
  const hasActions = mayOpen || mayEdit || mayClone || mayDelete;
  // Die Vorlagen fuer „Duplizieren" sind alle Arbeitsbereiche, auch wenn diese
  // Stelle nur die eines Kontos zeigt — kopiert wird von ueberall her. Die
  // Abfrage ist dieselbe, die der Aufrufer schon gestellt hat.
  // `isLoading` derselben Abfrage: solange sie laeuft, zeigt die Tabelle ihr
  // Geruest statt einer leeren Liste, die es nur noch nicht ist.
  const { data: allTenants, isLoading: tenantsLoading } = useListTenants();
  const { data: cloneJobsData } = useCloneJobs();
  const deleteTenant = useDeleteTenant();
  const discardCloneJob = useDiscardCloneJob();
  // Destructured, not held as an object: react-query returns a fresh result
  // literal every render, while `mutateAsync` is bound once in the mutation
  // observer's constructor. Depending on the object would rebuild every column
  // and every row cell of the table below on every render.
  const { mutateAsync: dismissCloneJob, isPending: isDismissingCloneJob } = useDismissCloneJob();
  // Wo die Arbeitsbereiche stehen, gehoert auch die Auffrischung hin: der
  // Kopierauftrag-Haken macht sie bewusst nicht selbst.
  useRefreshTenantsWhenACloneJobFinishes(cloneJobsData?.jobs);

  const [editingTenant, setEditingTenant] = useState<Tenant | undefined>(undefined);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloningTenant, setCloningTenant] = useState<Tenant | undefined>(undefined);
  const [deletingTenant, setDeletingTenant] = useState<Tenant | undefined>(undefined);
  const [discardingJob, setDiscardingJob] = useState<TenantCloneJob | undefined>(undefined);

  // Merged client-side: the clone jobs are their own lightweight endpoint, so
  // `list-tenants` never learns about them.
  const cloneJobsByTenantId = useMemo(
    () => new Map((cloneJobsData?.jobs ?? []).map((job) => [job.tenantId, job])),
    [cloneJobsData?.jobs]
  );

  // BOTH ends of a running copy: `delete-tenant` refuses for the source as well
  // as the target, so leaving the source's actions enabled only moves the
  // failure to after the destructive confirm dialog.
  const tenantIdsBeingCloned = useMemo(
    () =>
      new Set(
        (cloneJobsData?.jobs ?? [])
          .filter((job) => job.status === 'running')
          .flatMap((job) => [job.tenantId, job.sourceTenantId])
      ),
    [cloneJobsData?.jobs]
  );

  // Only clones whose TENANT still needs a decision. A kept tenant's clone
  // succeeded, so a discard is refused by design — those get a row badge with
  // its own dismiss. The partition itself lives in contracts, so this filter and
  // the backend's discard guard cannot disagree about it.
  const jobsNeedingDecision = useMemo(
    () =>
      (cloneJobsData?.jobs ?? []).filter(
        (job) => tenantCloneJobDisposition(job.status) === 'needsDecision'
      ),
    [cloneJobsData?.jobs]
  );

  const handleEdit = useCallback((tenant: Tenant) => {
    setEditingTenant(tenant);
    setEditDialogOpen(true);
  }, []);

  const handleCloneClick = useCallback((tenant: Tenant) => {
    setCloningTenant(tenant);
    setCloneDialogOpen(true);
  }, []);

  const handleDeleteClick = useCallback((tenant: Tenant) => {
    setDeletingTenant(tenant);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingTenant) return;

    try {
      await deleteTenant.mutateAsync(deletingTenant._id);
      toast.success(t('tenantAdmin.deleteSuccess'));
      setDeletingTenant(undefined);
    } catch (error) {
      toast.error(t('tenantAdmin.deleteFailed'));
      console.error('Failed to delete tenant:', error);
    }
  };

  const handleViewTenant = useCallback(
    (tenant: Tenant) => {
      navigate({ to: '/admin/tenants/$tenantId', params: { tenantId: tenant._id } });
    },
    [navigate]
  );

  const handleDiscardConfirm = async () => {
    if (!discardingJob) return;

    try {
      const { jobRetained } = await discardCloneJob.mutateAsync(discardingJob.jobId);
      // A partially rolled-back discard keeps its job so the leftovers stay
      // visible — reporting a clean rollback that did not happen is worse than
      // the leftovers themselves.
      if (jobRetained) {
        toast.warning(t('tenantAdmin.cloneDiscardPartial'));
      } else {
        toast.success(t('tenantAdmin.cloneDiscardSuccess'));
      }
      setDiscardingJob(undefined);
    } catch (error) {
      // Renders the backend's own code — a refused discard (the clone finished
      // after all) has to say exactly that, not a generic failure.
      toast.error(translateErrorMessage(error as ApiError, t));
    }
  };

  const handleDismiss = useCallback(
    async (job: TenantCloneJob) => {
      try {
        await dismissCloneJob(job.jobId);
        toast.success(t('tenantAdmin.cloneDismissSuccess'));
      } catch (error) {
        toast.error(translateErrorMessage(error as ApiError, t));
      }
    },
    [dismissCloneJob, t]
  );

  const columns = useMemo<DataTableColumnDef<TenantListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: createColumnHeader(t('tenantAdmin.columnName')),
        meta: { label: t('tenantAdmin.columnName'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const tenant = row.original;
          const cloneJob = cloneJobsByTenantId.get(tenant._id);
          let logoUrl: string | undefined;

          if (tenant.logo) {
            if (tenant.logo.type === 'svg') {
              logoUrl = tenant.logo.svg;
            } else {
              logoUrl = tenant.logo.thumbnail?.webp || tenant.logo.thumbnail?.png;
            }
          }

          return (
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 shrink-0 flex items-center justify-center">
                {logoUrl && (
                  <img src={logoUrl} alt={tenant.name} className="h-8 w-8 object-contain" />
                )}
              </div>
              {mayOpen ? (
                <span
                  className="font-medium cursor-pointer hover:text-primary hover:underline"
                  onClick={() => handleViewTenant(tenant)}
                >
                  {tenant.name}
                </span>
              ) : (
                <span className="font-medium">{tenant.name}</span>
              )}
              {cloneJob && (
                <CloneJobBadge
                  job={cloneJob}
                  onDismiss={() => handleDismiss(cloneJob)}
                  isDismissing={isDismissingCloneJob}
                />
              )}
            </div>
          );
        },
        enableGlobalFilter: true
      },
      {
        accessorKey: 'slug',
        header: createColumnHeader(t('tenantAdmin.columnSlug')),
        meta: { label: t('tenantAdmin.columnSlug'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => <span className="text-muted-foreground">{row.getValue('slug')}</span>,
        enableGlobalFilter: true
      },
      {
        accessorKey: 'description',
        header: createColumnHeader(t('tenantAdmin.columnDescription')),
        meta: { label: t('tenantAdmin.columnDescription'), filterType: 'text' },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const description = row.original.description;
          if (!description) return null;
          return (
            <span
              className="block max-w-[200px] truncate text-muted-foreground"
              title={description}
            >
              {description}
            </span>
          );
        },
        enableGlobalFilter: true
      },
      {
        accessorKey: 'tenantType',
        header: createColumnHeader(t('tenantAdmin.columnType')),
        meta: { label: t('tenantAdmin.columnType') },
        cell: ({ row }) => <TenantTypeBadge tenantType={row.original.tenantType} />
      },
      {
        // Das Konto ist die Ebene darüber (LIC-06): von hier aus hin, nicht
        // nur vom Konto zu seinen Arbeitsbereichen. Der Name liegt in der
        // Suche, damit „alle Arbeitsbereiche von Vitalis" eine Eingabe ist.
        id: 'account',
        accessorFn: (row) => row.accountName ?? '',
        header: createColumnHeader(t('tenantAdmin.columnAccount')),
        meta: { label: t('tenantAdmin.columnAccount') },
        cell: ({ row }) => {
          const { accountId, accountName } = row.original;
          if (!accountId) return <span className="text-muted-foreground">–</span>;
          return (
            <Link
              to="/admin/accounts/$accountId"
              params={{ accountId }}
              className="hover:underline"
              onClick={(event) => event.stopPropagation()}
            >
              {accountName ?? t('accountAdmin.unnamed')}
            </Link>
          );
        }
      },
      {
        accessorKey: 'active',
        header: createColumnHeader(t('tenantAdmin.columnStatus')),
        meta: {
          label: t('tenantAdmin.columnStatus'),
          filterType: 'select',
          filterOptions: [
            { label: t('tenantAdmin.statusActive'), value: 'true' },
            { label: t('tenantAdmin.statusInactive'), value: 'false' }
          ]
        },
        enableColumnFilter: true,
        cell: ({ row }) => {
          const active = row.getValue('active') as boolean;
          return (
            <Badge colorScheme={active ? 'success' : 'error'}>
              {active ? t('tenantAdmin.statusActive') : t('tenantAdmin.statusInactive')}
            </Badge>
          );
        }
      },
      {
        id: 'members',
        accessorFn: (row) => row.memberCount ?? 0,
        header: createColumnHeader(t('tenantAdmin.columnMembers')),
        meta: { label: t('tenantAdmin.columnMembers') },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium">{row.original.memberCount ?? 0}</span>
        )
      },
      {
        id: 'processes',
        accessorFn: (row) => row.processCount ?? 0,
        header: createColumnHeader(t('tenantAdmin.columnProcesses')),
        meta: { label: t('tenantAdmin.columnProcesses') },
        cell: ({ row }) => (
          <span className="tabular-nums font-medium">{row.original.processCount ?? 0}</span>
        )
      },
      {
        accessorKey: 'expiresAt',
        header: createColumnHeader(t('tenantAdmin.columnExpires')),
        meta: { label: t('tenantAdmin.columnExpires') },
        cell: ({ row }) => <TenantExpiryBadge expiresAt={row.original.expiresAt} />
      },
      // Editing, cloning or deleting a tenant mid-copy races a detached worker
      // that would happily re-provision what a delete just revoked.
      ...(hasActions
        ? [
            createActionsColumn<TenantListItem>({
              onView: mayOpen ? handleViewTenant : undefined,
              onEdit: mayEdit ? handleEdit : undefined,
              onClone: mayClone ? handleCloneClick : undefined,
              onDelete: mayDelete ? handleDeleteClick : undefined,
              isRowDisabled: (tenant) => tenantIdsBeingCloned.has(tenant._id)
            })
          ]
        : [])
    ],
    [
      mayOpen,
      mayEdit,
      mayClone,
      mayDelete,
      hasActions,
      handleEdit,
      handleDeleteClick,
      handleViewTenant,
      handleCloneClick,
      handleDismiss,
      cloneJobsByTenantId,
      tenantIdsBeingCloned,
      isDismissingCloneJob,
      t
    ]
  );

  return (
    <>
      {/* Clones that stopped and need an explicit decision */}
      {jobsNeedingDecision.length > 0 && (
        <div className="mb-4 space-y-3">
          {jobsNeedingDecision.map((job) => (
            <CloneJobAlert
              key={job.jobId}
              job={job}
              onDiscard={() => setDiscardingJob(job)}
              onDismiss={() => handleDismiss(job)}
              isDismissing={isDismissingCloneJob}
            />
          ))}
        </div>
      )}

      <DataTable
        columns={columns}
        data={tenants}
        isLoading={tenantsLoading}
        keepsLayoutWhenEmpty={keepsLayoutWhenEmpty}
        enableGlobalFilter
        enableColumnVisibility
        enableColumnFilters
        enableUrlState
        tableId={tableId}
        globalSearchPlaceholder={t('tenantAdmin.searchPlaceholder')}
        initialColumnVisibility={initialColumnVisibility}
      />

      {/* Bearbeiten gehoert zur Zeile — der Einstieg zum Anlegen zur Seite. */}
      <CreateTenantDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        editingTenant={editingTenant}
      />

      {/* Bedingt gehängt, damit jedes Öffnen mit frischen Vorgabewerten beginnt. */}
      {cloneDialogOpen && (
        <CloneTenantDialog
          open={cloneDialogOpen}
          onOpenChange={setCloneDialogOpen}
          source={cloningTenant}
          candidates={allTenants?.tenants ?? []}
        />
      )}

      {/* Mounted conditionally so the typed name is destroyed with it,
          whichever path closes it. */}
      {deletingTenant && (
        <DeleteTenantConfirmDialog
          open
          onOpenChange={(open) => !open && setDeletingTenant(undefined)}
          onConfirm={handleDeleteConfirm}
          isDeleting={deleteTenant.isPending}
          tenantName={deletingTenant.name}
        />
      )}

      {/* Discard Confirmation Dialog — drops the half-built clone's database.
          Mounted conditionally so the typed-name gate is destroyed with it: a
          partial discard keeps its job, and a retry must be typed out again. */}
      {discardingJob && (
        <DiscardCloneConfirmDialog
          open
          onOpenChange={(open) => !open && setDiscardingJob(undefined)}
          onConfirm={handleDiscardConfirm}
          isDiscarding={discardCloneJob.isPending}
          tenantName={discardingJob.tenantName}
        />
      )}
    </>
  );
}
